import React from "react";
import { BtnUi } from "~/components";
import { getAnalyticsClickTrackingAttributes } from "~/utils/adobeDataLayerUtils";
import styles from "./MembershipTier.module.css";
import classNames from "classnames";
import { Check } from "../Icon/Icons/PassionsHub/Check";
import { InfinityIcon } from "../Icon/Icons/PassionsHub/Infinity";
import { ArrowUpRight } from "../Icon/Icons/PassionsHub/ArrowUpRight";
import { CheckCircleBroken } from "../Icon/Icons/PassionsHub/CheckCircleBroken";
import { getTierGradient, getTierBenefits } from "./membershipTierUtils";

export const MembershipTier = ({
  tierName,
  nextTierName,
  currentProgress,
  targetProgress,
  progressUnit = "nights",
  benefits = [],
  loading = false,
  className,
  ...props
}) => {
  // Use fallback values only after loading is complete
  const displayTierName = tierName || "Silver Elite";
  const displayCurrentProgress = currentProgress ?? 0;
  const displayTargetProgress = targetProgress ?? 2;

  const progressPercentage = Math.min(
    (displayCurrentProgress / displayTargetProgress) * 100,
    100,
  );
  const remainingToTarget = Math.max(
    displayTargetProgress - displayCurrentProgress,
    0,
  );

  // Use nextTierName if provided, otherwise fall back to tierName for display
  const displayNextTier = nextTierName || displayTierName;

  // Get gradient for current tier and benefits for next tier
  const currentGradient = getTierGradient(displayTierName);
  const displayBenefits =
    benefits.length > 0 ? benefits : getTierBenefits(displayNextTier);

  // Generate unique gradient ID for this component instance
  const gradientId = `progressGradient-${displayTierName.replace(/\s+/g, "-").toLowerCase()}`;

  // Render loading skeleton
  if (loading) {
    return (
      <div
        className={classNames(styles.membershipTier, styles.loading, className)}
        {...props}
      >
        <div className={styles.header}>
          <div className={styles.logoContainer}>
            <div className={classNames(styles.logo, styles.skeleton)} />
          </div>
          <div className={classNames(styles.titleSkeleton, styles.skeleton)} />
          <div
            className={classNames(styles.subtitleSkeleton, styles.skeleton)}
          />
        </div>
        <div className={styles.progressSection}>
          <div
            className={classNames(
              styles.progressCircleSkeleton,
              styles.skeleton,
            )}
          />
        </div>
        <div className={styles.benefitsSection}>
          <div className={styles.benefitsGrid}>
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className={classNames(styles.benefitSkeleton, styles.skeleton)}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={classNames(styles.membershipTier, className)} {...props}>
      <div className={styles.header}>
        <div className={styles.logoContainer}>
          <div className={styles.logo}>
            <InfinityIcon />
          </div>
        </div>

        <h2 className={styles.title}>
          Your next tier of exclusive benefits is almost unlocked.
        </h2>

        <p className={styles.subtitle}>
          What&apos;s included in {displayNextTier}
        </p>
      </div>

      <div className={styles.progressSection}>
        <div className={styles.progressCircle}>
          <svg className={styles.progressSvg} viewBox="0 0 120 120">
            <defs>
              <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor={currentGradient.start} />
                <stop offset="100%" stopColor={currentGradient.end} />
              </linearGradient>
            </defs>
            <path
              d="M 20 60 A 40 40 0 0 1 100 60"
              className={styles.progressBackground}
            />
            <path
              d="M 20 60 A 40 40 0 0 1 100 60"
              className={styles.progressForeground}
              stroke={`url(#${gradientId})`}
              strokeDasharray={`${(progressPercentage / 100) * 125.66} 125.66`}
              strokeDashoffset="0"
            />
          </svg>

          {/* Progress end icon */}
          {progressPercentage > 0 && (
            <div
              className={styles.progressIcon}
              style={{
                left: `${((60 + 40 * Math.cos(Math.PI * (1 - progressPercentage / 100))) / 120) * 100}%`,
                top: `${((60 - 40 * Math.sin(Math.PI * (1 - progressPercentage / 100))) / 120) * 100}%`,
                backgroundColor: currentGradient.end,
              }}
            >
              <Check className={styles.progressEndIcon} />
            </div>
          )}

          <div className={styles.progressContent}>
            <div className={styles.progressNumber}>{remainingToTarget}</div>
            <div className={styles.progressText}>
              more {progressUnit} to {displayNextTier}
            </div>
          </div>
        </div>
      </div>

      <div className={styles.benefitsSection}>
        <div className={styles.benefitsGrid}>
          {displayBenefits.map((benefit, index) => (
            <div key={index} className={styles.benefitItem}>
              <CheckCircleBroken />
              <span className={styles.benefitText}>
                {benefit.text || benefit}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className={styles.actionSection}>
        <BtnUi
          onClick={() =>
            window.open(
              "https://www.marriott.com/loyalty/member-benefits.mi",
              "_blank",
            )
          }
          variant="primary"
          className={classNames(styles.learnMoreBtn, "custom_click_track")}
          {...getAnalyticsClickTrackingAttributes(
            "Membership Tier",
            "click",
            "Learn more",
          )}
        >
          Learn more
          <ArrowUpRight className={styles.arrowIcon} />
        </BtnUi>
      </div>
    </div>
  );
};
